import { Slot } from 'expo-router';
import { Platform } from 'react-native';
import { SessionProvider } from '../lib/SessionContext';
import { AuthProvider } from '../lib/AuthContext';
import { QueryProvider } from '../lib/QueryProvider';
import AuthModal from '../components/AuthModal';
import CookieConsentBanner from '../components/CookieConsentBanner';
import * as Sentry from '@sentry/react-native';
import type { ErrorEvent } from '@sentry/react-native';
import React from 'react';

import { ActionSheetProvider } from '@expo/react-native-action-sheet';
import { FontProvider } from '../lib/FontContext';
import Intercom from '@intercom/messenger-js-sdk';
import SupportChat from '../components/SupportChat';
import { supportProvider } from '../lib/supportRuntime';
import { getOrCreateSignupAttribution } from '../lib/signupAttribution';
import CrashRecoveryBoundary from '../components/CrashRecoveryBoundary';
import PublicWebExportBoundary from '../components/PublicWebExportBoundary';
import { AppI18nProvider } from '../lib/i18n/AppI18nProvider';
import {
    sanitizeSensitiveBreadcrumbData,
    sanitizeSensitiveRequest,
} from '../lib/sensitiveUrl';
import {
    isKnownBrowserExtensionInpageSentryEvent,
    isKnownCefSharpObjectNotFoundSentryEvent,
    isKnownIntercomFrameModernSentryEvent,
    isKnownIntercomReduxSentryEvent,
    isNonTerminalNetworkSentryEvent,
} from '../lib/sentryNoise';

// Import KaTeX CSS for web (LaTeX rendering in tooltips)
if (Platform.OS === 'web') {
    require('katex/dist/katex.min.css');
    require('../assets/responsive.css');
}

const thirdPartyTelemetryAllowed = Platform.OS !== 'web'
    || (typeof window !== 'undefined'
        && (window as Window & {
            __PZ_ANALYTICS_ALLOWED?: boolean;
        }).__PZ_ANALYTICS_ALLOWED === true);

Sentry.init({
    dsn: "https://d6084c85359c6ed8a22b21910724992a@o4509503680020480.ingest.de.sentry.io/4509503682707536",
    debug: false,
    // The pre-hydration guard runs before this bundle. A page that arrived
    // with an auth capability stays completely third-party-telemetry-free,
    // including when history.replaceState could not scrub the address bar.
    enabled: thirdPartyTelemetryAllowed,
    enableNative: Platform.OS !== 'web',
    ...(process.env.NODE_ENV === 'production' && {
        tunnel: "/sentry-tunnel",
    }),

    // trim strings & deep objects automatically
    maxValueLength: 5000,     // truncate long strings  → "foo…"
    normalizeDepth: 3,        // stop walking after 3 nested levels

    beforeSend(event: ErrorEvent) {
        if (
            isKnownIntercomReduxSentryEvent(event) ||
            isKnownIntercomFrameModernSentryEvent(event) ||
            isKnownBrowserExtensionInpageSentryEvent(event) ||
            isKnownCefSharpObjectNotFoundSentryEvent(event) ||
            isNonTerminalNetworkSentryEvent(event)
        ) {
            return null;
        }

        if (event.request) {
            sanitizeSensitiveRequest(event.request);
        }
        event.breadcrumbs?.forEach((breadcrumb) => {
            sanitizeSensitiveBreadcrumbData(breadcrumb.data);
        });

        return event;
    },

    beforeBreadcrumb(breadcrumb, hint) {
        if (
            breadcrumb.category === 'fetch' ||              // network breadcrumbs
            breadcrumb.category === 'xhr'
        ) {
            const url = breadcrumb.data?.url;
            if (url?.startsWith('data:')) {
                // Option A – drop the breadcrumb completely
                return null;

                // Option B – keep a short placeholder instead
                // breadcrumb.data.url = `${url.slice(0,100)}…[truncated]`;
            }
        }
        sanitizeSensitiveBreadcrumbData(breadcrumb.data);
        return breadcrumb;
    },
});

if (thirdPartyTelemetryAllowed && supportProvider === 'intercom') {
    Intercom({
        app_id: 'f2k1536r',
    });
}

function AppLayout() {
    // Route-group layouts handle routing mode and auth guards.
    return (
        <PublicWebExportBoundary>
            <Slot />
            <AuthModal />
            <SupportChat />
            <CookieConsentBanner />
        </PublicWebExportBoundary>
    );
}

function RootLayout() {
    React.useEffect(() => {
        // Capture first-touch attribution before URL params can be stripped.
        getOrCreateSignupAttribution();
    }, []);

    // Always render immediately - no blocking
    return (
        <CrashRecoveryBoundary>
            <FontProvider>
                <ActionSheetProvider>
                    <QueryProvider>
                        <SessionProvider>
                            <AppI18nProvider>
                                <AuthProvider>
                                    <AppLayout />
                                </AuthProvider>
                            </AppI18nProvider>
                        </SessionProvider>
                    </QueryProvider>
                </ActionSheetProvider>
            </FontProvider>
        </CrashRecoveryBoundary>
    );
}

export default Sentry.wrap(RootLayout);
